import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronRight, Leaf, ExternalLink, Sprout, Droplets, Globe2, TrendingDown, Fish } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

const FIGURE_BAR_CHART = 'https://images.squarespace-cdn.com/content/v1/5df844d027a44801a0f30f64/1580699351906-YV5YNVHHU6CNV4ORQUB9/figure04-2kG.png';
const FIGURE_SDGS = 'https://images.squarespace-cdn.com/content/v1/5df844d027a44801a0f30f64/1580700865520-U4DU09URCFFG79EIRBSY/figure03-SDGs.png';

const STATS = [
  { Icon: TrendingDown, value: '34.5%', label: 'of the global environmental footprint traces back to animal products — larger than crops and coal combined.' },
  { Icon: Fish, value: '58 billion', label: 'land and aquatic animals are killed each year to produce ~294.7M metric tons of meat.' },
  { Icon: Droplets, value: '#1', label: 'cause of the destruction of the natural environment, per UN environmental data.' },
];

const LEVERS = [
  { Icon: Leaf, title: 'Plant-based, always', body: 'Every VDBF SKU is 100% plant-based — zero dairy, zero eggs, zero honey. No compromise recipes.' },
  { Icon: Sprout, title: 'Ingredient partners we trust', body: 'We source cultured plant cheese and dairy alternatives from Daiya and Yoso, and plant-based meats from TMRW — Canadian brands, tightly-audited supply chains.' },
  { Icon: Globe2, title: 'Frozen-to-oven efficiency', body: 'Baking from frozen removes proof-and-thaw energy from the operator side and cuts overproduction waste at the case level.' },
];

export default function Sustainability() {
  return (
    <main className="min-h-screen bg-background">
      <Header variant="solid" />

      {/* HERO */}
      <section className="relative pt-28 pb-16 md:pt-36 md:pb-24 overflow-hidden">
        <div className="absolute inset-0 bg-grain opacity-60 pointer-events-none" />
        <div className="absolute -top-32 -left-32 h-[28rem] w-[28rem] rounded-full bg-leaf-soft/40 blur-3xl pointer-events-none" />
        <div className="absolute top-20 -right-32 h-[26rem] w-[26rem] rounded-full bg-honey-soft/50 blur-3xl pointer-events-none" />

        <div className="container mx-auto relative">
          <div className="text-sm text-muted-foreground flex items-center gap-2">
            <Link to="/" className="hover:text-primary">Home</Link>
            <ChevronRight className="h-3.5 w-3.5" />
            <span className="text-primary font-medium">Sustainability</span>
          </div>

          <div className="mt-8 max-w-3xl">
            <div className="font-script text-2xl text-leaf">shared responsibility</div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl text-primary font-medium leading-[1.02] mt-2 text-balance">
              A plant is a<br/><span className="italic font-light text-leaf">climate decision.</span>
            </h1>
            <p className="mt-6 text-base md:text-lg text-muted-foreground max-w-2xl text-pretty">
              According to UN environmental data, <strong className="text-primary font-medium">animal products are the number-one cause of the destruction of the natural environment</strong> — larger than crops and coal combined. Choosing plant-based food is the single most powerful daily lever any of us has for a livable planet.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Badge className="bg-secondary text-primary hover:bg-secondary rounded-full px-3 py-1"><Leaf className="h-3 w-3 mr-1 text-leaf" /> UN SDG 12 · Responsible Consumption</Badge>
              <Badge className="bg-secondary text-primary hover:bg-secondary rounded-full px-3 py-1"><Globe2 className="h-3 w-3 mr-1 text-leaf" /> UN SDG 13 · Climate Action</Badge>
            </div>
          </div>
        </div>
      </section>

      {/* KEY STATS */}
      <section className="py-14 md:py-20 bg-parchment/40">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-3 gap-5">
            {STATS.map(({ Icon, value, label }, i) => (
              <motion.div
                key={value}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.55, delay: i * 0.08 }}
                className="rounded-3xl bg-card border border-border/70 p-7 shadow-soft"
              >
                <div className="h-12 w-12 rounded-2xl gradient-leaf grid place-items-center text-primary-foreground shadow-card">
                  <Icon className="h-5 w-5" />
                </div>
                <div className="font-display text-4xl md:text-5xl font-medium text-primary mt-5 leading-none">{value}</div>
                <p className="text-sm text-muted-foreground leading-relaxed mt-3">{label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* BAR CHART FIGURE */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5">
            <div className="font-script text-2xl text-leaf">the data</div>
            <h2 className="font-display text-3xl sm:text-4xl text-primary font-medium leading-tight mt-1">
              Animal products lead every<br/><span className="italic font-light text-leaf">environmental impact category.</span>
            </h2>
            <p className="text-muted-foreground text-base mt-5">
              The UN Environment Programme's Sustainable Consumption and Production Branch measures our
              collective environmental footprint across ten resource categories. Weighted for
              Global Warming Potential, Land Use Competition and Human Toxicity, one category dominates them all.
            </p>
            <ul className="mt-6 space-y-2.5 text-sm">
              <li className="flex items-center justify-between border-b border-border/60 pb-2.5"><span className="text-primary font-medium">Animal Products</span><span className="font-display text-leaf text-lg font-semibold">34.5%</span></li>
              <li className="flex items-center justify-between border-b border-border/60 pb-2.5"><span className="text-muted-foreground">Crops</span><span className="font-display text-primary/80">18.6%</span></li>
              <li className="flex items-center justify-between border-b border-border/60 pb-2.5"><span className="text-muted-foreground">Coal</span><span className="font-display text-primary/80">14.8%</span></li>
              <li className="flex items-center justify-between text-xs text-muted-foreground pt-1"><span>Remaining seven categories</span><span>32.1%</span></li>
            </ul>
            <div className="mt-6 text-xs text-muted-foreground italic">
              Source: UNEP Sustainable Consumption and Production Branch — <em>Assessing the Environmental Impacts of Consumption and Production: Priority Products and Materials.</em>
            </div>
          </div>
          <div className="lg:col-span-7">
            <figure className="rounded-3xl overflow-hidden bg-card border border-border/70 shadow-card p-4 sm:p-8">
              <img
                src={FIGURE_BAR_CHART}
                alt="Environmental damage indicators by resource category — UNEP Sustainable Consumption and Production Branch"
                className="w-full h-auto"
                loading="lazy"
              />
              <figcaption className="text-xs text-muted-foreground italic mt-4 text-center px-4">
                Environmental damage indicators as reported by the United Nations Environment Programme (UNEP) Sustainable Consumption and Production Branch.
              </figcaption>
            </figure>
          </div>
        </div>
      </section>

      {/* SDG FIGURE */}
      <section className="py-16 md:py-24 bg-parchment/40">
        <div className="container mx-auto grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 order-2 lg:order-1">
            <figure className="rounded-3xl overflow-hidden bg-card border border-border/70 shadow-card p-4 sm:p-8">
              <img
                src={FIGURE_SDGS}
                alt="Responsible Consumption and Production (Goal 12) at the center of the UN Sustainable Development Goals"
                className="w-full h-auto"
                loading="lazy"
              />
              <figcaption className="text-xs text-muted-foreground italic mt-4 text-center px-4">
                Responsible Consumption and Production (Goal 12) is at the center of several UN Sustainable Development Goals.
              </figcaption>
            </figure>
          </div>
          <div className="lg:col-span-5 order-1 lg:order-2">
            <div className="font-script text-2xl text-leaf">the framework</div>
            <h2 className="font-display text-3xl sm:text-4xl text-primary font-medium leading-tight mt-1">
              Responsible consumption<br/><span className="italic font-light text-leaf">sits at the center.</span>
            </h2>
            <p className="text-muted-foreground text-base mt-5">
              UN Sustainable Development Goal 12 — Responsible Consumption and Production — is the
              hinge that unlocks progress across climate action, life on land, life below water,
              zero hunger, clean water and good health. What we eat is the most concrete lever any
              individual has to move that hinge.
            </p>
            <div className="mt-6 rounded-2xl bg-card border border-border/70 p-5">
              <p className="text-sm text-primary italic leading-relaxed">
                "Animal products are, by far, the greatest environmental problem. Please always
                choose plant-based foods."
              </p>
              <div className="text-xs text-muted-foreground mt-3">— Dr. Azam Khan, PhD (University of Copenhagen)</div>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT WE DO */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto">
          <div className="max-w-2xl">
            <div className="font-script text-2xl text-leaf">our commitment</div>
            <h2 className="font-display text-4xl sm:text-5xl text-primary font-medium leading-tight mt-1">
              What Vegan Danish<br/><span className="italic font-light text-leaf">does about it.</span>
            </h2>
            <p className="text-muted-foreground text-base md:text-lg mt-4">
              Every product on our roster is engineered to make the lower-impact choice the easier
              choice — for students, operators, and hospitality teams.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
            {LEVERS.map(({ Icon, title, body }) => (
              <div key={title} className="relative rounded-3xl bg-card border border-border/70 p-7 shadow-soft hover:shadow-card hover:-translate-y-1 transition-all duration-500">
                <div className="h-12 w-12 rounded-2xl gradient-leaf grid place-items-center text-primary-foreground shadow-card">
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className="font-display text-xl font-semibold text-primary mt-5">{title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed mt-2">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* REFERENCES — the two source reports */}
      <section className="py-16 md:py-24 bg-primary text-primary-foreground">
        <div className="container mx-auto">
          <div className="max-w-2xl">
            <div className="font-script text-2xl text-accent">the source reports</div>
            <h3 className="font-display text-3xl sm:text-4xl font-medium leading-tight mt-1">
              Read the primary evidence.
            </h3>
            <p className="text-primary-foreground/80 mt-4 max-w-2xl text-base">
              The figures and citations above are drawn directly from two landmark United Nations
              reports on the environmental impact of consumption — including the UN FAO's
              foundational study of the livestock sector. Both are open-access PDFs.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mt-12">
            {[
              {
                title: 'Assessing the Environmental Impacts of Consumption and Production',
                subtitle: 'Priority Products and Materials',
                org: 'UNEP · International Panel for Sustainable Resource Management',
                cover: 'https://customer-assets.emergentagent.com/job_vegan-danish-bakery/artifacts/8u84vqmt_UNEP-Impacts-Cover.png',
                pdf: 'https://customer-assets.emergentagent.com/job_vegan-danish-bakery/artifacts/jr4b76tl_UNEP-EnvironmentalImpactsofConsumptionandProduction.pdf',
                blurb: 'The UN Environment Programme report that identifies animal products as the largest single category of global environmental impact — larger than crops and coal combined.'
              },
              {
                title: "Livestock's Long Shadow",
                subtitle: 'Environmental Issues and Options',
                org: 'UN FAO · Livestock, Environment and Development (LEAD) Initiative',
                cover: 'https://customer-assets.emergentagent.com/job_vegan-danish-bakery/artifacts/flt9u8tk_UN-LivestocksLongShadow-Cover.png',
                pdf: 'https://customer-assets.emergentagent.com/job_vegan-danish-bakery/artifacts/kq934gqw_UN-Livestocks%20Long%20Shadow.pdf',
                blurb: "The UN Food and Agriculture Organization's foundational assessment of the environmental toll of the livestock sector across land, water, biodiversity and greenhouse-gas emissions."
              }
            ].map((r) => (
              <motion.a
                key={r.title}
                href={r.pdf}
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.55 }}
                className="group flex flex-col sm:flex-row gap-6 rounded-3xl bg-primary-foreground/5 border border-primary-foreground/15 p-6 hover:bg-primary-foreground/10 hover:-translate-y-1 transition-all duration-500"
              >
                <div className="shrink-0 w-full sm:w-40 aspect-square sm:aspect-[3/4] rounded-2xl overflow-hidden bg-primary-foreground/5 shadow-pop">
                  <img
                    src={r.cover}
                    alt={`${r.title} — cover`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                </div>
                <div className="flex flex-col flex-1">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-accent font-medium">{r.org}</div>
                  <h4 className="font-display text-xl font-semibold mt-2 leading-tight group-hover:text-accent transition-colors">{r.title}</h4>
                  <div className="text-sm text-primary-foreground/70 italic mt-1">{r.subtitle}</div>
                  <p className="text-sm text-primary-foreground/80 leading-relaxed mt-3">{r.blurb}</p>
                  <div className="mt-auto pt-4 inline-flex items-center gap-1.5 text-sm font-medium text-accent">
                    Read the full PDF <ExternalLink className="h-3.5 w-3.5" />
                  </div>
                </div>
              </motion.a>
            ))}
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-3">
            <Button asChild variant="outline" className="rounded-full border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10 h-12 bg-transparent">
              <Link to="/products">See our plant-based line</Link>
            </Button>
            <span className="text-xs text-primary-foreground/60">Also referenced: UN Sustainable Development Goals 11, 12 &amp; 13.</span>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
